/**
 * Lightweight Gift-With-Purchase helper.
 *
 * Attach a data node to your cart drawer or cart page:
 *
 * <div
 *   data-gwp-root
 *   data-gwp-variant-id="{{ 1234567890 }}"
 *   data-gwp-threshold-cents="{{ 12000 }}"
 *   data-gwp-remove-when-below="true"
 * ></div>
 *
 * Then include this script once (e.g. in cart-drawer.liquid and cart.liquid):
 * {{ 'gift-with-purchase.js' | asset_url | script_tag }}
 */

(function giftWithPurchaseController() {
  const root = document.querySelector('[data-gwp-root]');
  if (!root) {
    return;
  }

  const variantId = Number(root.dataset.gwpVariantId);
  if (!variantId) {
    console.warn('[GWP] Missing or invalid data-gwp-variant-id attribute.');
    return;
  }

  const thresholdCents = Number(root.dataset.gwpThresholdCents || root.dataset.gwpThreshold || 0);
  if (!Number.isFinite(thresholdCents) || thresholdCents <= 0) {
    console.warn('[GWP] Missing or invalid data-gwp-threshold-cents attribute.');
    return;
  }

  const removeWhenBelow = root.dataset.gwpRemoveWhenBelow !== 'false';
  const autoFlag = '_auto_added_gwp';

  let busy = false;
  let scheduled = false;

  const jsonHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };

  const fetchJson = (url, options = {}) =>
    fetch(url, { ...options, headers: { ...jsonHeaders, ...(options.headers || {}) } }).then((response) => {
      if (!response.ok) {
        const error = new Error(`[GWP] Request failed: ${response.status}`);
        error.response = response;
        throw error;
      }
      return response.json();
    });

  function scheduleSync() {
    if (scheduled || busy) {
      return;
    }
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      syncCartState();
    });
  }

  function triggerThemeRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function syncCartState() {
    if (busy) {
      return;
    }

    busy = true;
    fetchJson('/cart.js')
      .then((cart) => {
        const subtotal = cart.items_subtotal_price;
        const meetsThreshold = subtotal >= thresholdCents;

        const giftLine = cart.items.find((item) => {
          if (Number(item.id) !== variantId) {
            return false;
          }
          return item.properties && item.properties[autoFlag] === 'true';
        });

        if (meetsThreshold && !giftLine) {
          return addGift();
        }

        if (!meetsThreshold && giftLine && removeWhenBelow) {
          return removeGift(giftLine.key);
        }

        return undefined;
      })
      .catch((error) => {
        console.error('[GWP] Failed to sync cart', error);
      })
      .finally(() => {
        busy = false;
      });
  }

  function addGift() {
    console.info('[GWP] Adding gift variant.');
    return fetchJson('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: {
          [autoFlag]: 'true'
        }
      })
    })
      .then(() => {
        triggerThemeRefresh();
      })
      .catch((error) => {
        console.error('[GWP] Failed to add gift', error);
      });
  }

  function removeGift(lineKey) {
    console.info('[GWP] Removing gift variant.');
    return fetchJson('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    })
      .then(() => {
        triggerThemeRefresh();
      })
      .catch((error) => {
        console.error('[GWP] Failed to remove gift', error);
      });
  }

  // Set up listeners for common cart update events (Dawn & similar themes).
  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);
  document.addEventListener('cart:change', scheduleSync);

  // Support cart drawer open events if dispatched.
  document.addEventListener('cart-drawer:opened', scheduleSync);
  document.addEventListener('cart-drawer:closed', scheduleSync);

  // Initial run once DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
})();
